import { inject, Injectable, Signal } from '@angular/core';
import { translateObjectSignal, translateSignal, TranslocoService } from '@jsverse/transloco';
import type { Language } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import { TranslationService } from '@kerberos-compliance/compliance-shared-ui';

@Injectable({
  providedIn: 'root',
})
export class AmlDeskTranslationService extends TranslationService {
  private readonly translocoService = inject(TranslocoService);

  public languages = translateObjectSignal('navigation.language') as Signal<Record<Language, string>>;

  /**
   * Sets the active language for the application.
   *
   * @param {Language} language - The language to be set as active.
   */
  public setLanguage(language: Language): void {
    this.translocoService.setActiveLang(language);
    this.activeLanguage.set(language);
  }

  /**
   * Translates a given key into its corresponding localized string, replacing any placeholders in the string with the provided parameters.
   *
   * @param {string | Signal<string>} key - The localization key or a signal containing the key to be translated.
   * @param {Signal<Record<string, unknown>> | Record<string, unknown>} [parameters] - Optional parameters or a signal of parameters used to replace placeholders in the localized string.
   * @returns {Signal<string>} A signal containing the translated string with placeholders replaced by the provided parameters.
   */
  public translateWithParams(
    key: string | Signal<string>,
    parameters?: Signal<Record<string, unknown>> | Record<string, unknown>,
  ): Signal<string> {
    return translateSignal(key, parameters ?? {});
  }

  /**
   * Translates an object using the given key and optional parameters.
   *
   * @template T - The type of the object to be translated.
   * @param {string | Signal<string>} key - The key used for translation can be a string or a Signal of a string.
   * @param {Signal<Record<string, unknown>> | Record<string, unknown>} [parameters] - Optional parameters to replace placeholders in the translation, can be a Signal of a record or a plain record.
   * @returns {Signal<T>} A Signal containing the translated object.
   */
  public translateObject<T>(
    key: string | Signal<string>,
    parameters?: Signal<Record<string, unknown>> | Record<string, unknown>,
  ): Signal<T> {
    return translateObjectSignal(key, parameters ?? {}) as Signal<T>;
  }
}
